/**
 * Реплики транскрипта и заземление доказательства по НОМЕРУ реплики
 * (спека §4; требование issue #860 от 21.09).
 *
 * До этого доказательством была строка, которую написала модель, и её
 * приходилось искать в транскрипте обратно — приёмом, нечётким по построению:
 * снимаются NFKC, регистр, пунктуация и пробелы, и сверх того допускается до
 * четверти несовпавших значимых токенов (`textPlausiblyContainsQuote`). У
 * ассистента #765 на ту же привязку ушло 22 раунда ревью, и рычага «смягчить»
 * в запасе нет — только «ужесточить».
 *
 * Номер реплики убирает весь класс: он либо есть в том, что мы отправили, либо
 * нет. Текст доказательства после этого берём МЫ — срезом по номеру, поэтому
 * «цитата, которой в разговоре не было» невозможна конструктивно, а не по
 * порогу совпадения.
 */

/** Реплика в том виде, в каком шаг отправляет её модели. */
export interface CriterionTranscriptTurn {
  /** Номер реплики, целый, с единицы: он же попадает в ответ модели. */
  turn: number;
  speaker: 'agent' | 'client' | 'unknown';
  text: string;
  /**
   * Индекс источника реплики, если вызывающий его передал
   * (`turnsFromTranscript` передаёт всегда). Куски одной длинной реплики несут
   * ОДИН И ТОТ ЖЕ индекс — они из одного сегмента.
   *
   * В промпт не попадает: `renderTurns` печатает названные поля, а не объект.
   */
  sourceIndex?: number;
}

/** Доказательство после заземления: текст взят из транскрипта, не от модели. */
export interface GroundedEvidence {
  turn: number;
  speaker: 'agent' | 'client' | 'unknown';
  /** НАШ срез реплики. Модель его не писала и испортить не может. */
  quote: string;
}

/** Сколько доказательств хранится у одного ответа. */
export const MAX_EVIDENCE_PER_ANSWER = 3;

/**
 * Потолок длины ОДНОЙ реплики — и отправляемой, и хранимой.
 *
 * Реплика приходит от вызывающего, и её длина ничем не ограничена: выгрузка
 * чата без разделителей строк — это одна реплика во весь разговор. Всё, что
 * длиннее, разрезается на куски со своими номерами (`prepareTurns`), поэтому
 * потолок держит заодно и размер доказательства в `record_signals.evidence`:
 * текст там — PII, и живёт он дольше самого транскрипта.
 *
 * 600 символов — примерно вдвое больше длинной реплики живого разговора:
 * обычную не режет, вырожденную ограничивает.
 */
export const MAX_TURN_CHARS = 600;

/**
 * Разделители строк внутри текста реплики.
 *
 * Список нужен в двух местах — при печати реплики в промпт и при нарезке
 * хранимого текста на реплики в замере, — поэтому живёт здесь один. Раньше он
 * был скопирован в оба, и правка одной копии означала расхождение нумерации с
 * печатью; сейчас от полноты этого списка не зависит ни один инвариант
 * (подделка номера закрыта формой `<turn>`, см. `@aica/prompt-builder`), он
 * остаётся вопросом читаемости и нарезки.
 */
// Задан кодами, а не escape-последовательностями в литерале регекспа: так рядом
// с каждым разделителем стоит его имя, и список читается без сверки с таблицей
// Unicode. Пара CR+LF даёт пустой кусок посередине — его снимает фильтр ниже.
const TURN_LINE_BREAK_CODES = [
  0x0a, // LF
  0x0d, // CR
  0x0b, // VT
  0x0c, // FF
  0x85, // NEL
  0x2028, // LINE SEPARATOR
  0x2029, // PARAGRAPH SEPARATOR
];
const TURN_LINE_BREAK = new RegExp(
  `[${String.fromCodePoint(...TURN_LINE_BREAK_CODES)}]`,
  'u'
);

/** Непустые строки текста, каждая без краевых пробелов. */
export function splitTurnLines(text: string): string[] {
  return text
    .split(TURN_LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line !== '');
}

/**
 * Реплика — ровно одна строка.
 *
 * Склейка сделана split/trim/join, а не регекспом `\s*\r?\n\s*`: тот
 * возвращается по длинному пробельному прогону на каждой позиции
 * (квадратично), а выгрузка чата такой прогон приносит. Ею же складывают
 * сегмент в строку промпта ассистент и редакция ПДн на границе модели
 * (`utils/prompt-redaction` workflows-api, #2148).
 */
export function flattenTurnText(text: string): string {
  return splitTurnLines(text).join(' ');
}

interface IndexedTurn {
  turn: number;
  speaker: 'agent' | 'client' | 'unknown';
  /** Текст реплики целиком — он же уходит в хранимое доказательство. */
  quote: string;
}

/**
 * Реплики, подготовленные к заземлению: пронумерованы и разобраны по номеру.
 *
 * Отдельный тип, а не голый массив, ровно потому, что построение карты — работа
 * на ЗАПИСЬ, а не на критерий: у организации их до тридцати, и повторять обход
 * двухсот реплик тридцать раз незачем.
 */
export interface CriterionTurnIndex {
  readonly byNumber: ReadonlyMap<number, IndexedTurn>;
  /** Сколько реплик отправлено — вырожденную нарезку видно по нему. */
  readonly size: number;
}

/**
 * Разрезать длинный текст на куски не длиннее потолка, по границе слова.
 *
 * Граница ищется назад от потолка: разрыв посреди слова читается хуже и в
 * панели выглядит ошибкой. Слова длиннее потолка не бывает в разговоре, но
 * если попалось — режем жёстко, иначе кусок вышел бы за предел.
 */
function chunkByWords(text: string, limit: number): string[] {
  if (text.length <= limit) return [text];
  const chunks: string[] = [];
  let rest = text;
  while (rest.length > limit) {
    const window = rest.slice(0, limit + 1);
    const cut = window.lastIndexOf(' ');
    const at = cut > 0 ? cut : limit;
    chunks.push(rest.slice(0, at).trim());
    rest = rest.slice(at).trim();
  }
  if (rest !== '') chunks.push(rest);
  return chunks;
}

/**
 * Пронумеровать реплики и подготовить их к отправке и заземлению.
 *
 * **Номера раздаёт эта функция, а не вызывающий.** Схема ответа принимает от
 * модели только целое с единицы, и реплика с номером 0 или 1.5 была бы
 * напечатана, но сослаться на неё модель не смогла бы — критерий молча остался
 * бы без сигнала. Повтор номера был бы хуже: промпт напечатал бы обе реплики, а
 * заземление оставило бы одну, и сохранённая цитата оказалась бы не тем, на что
 * сослалась модель. Оба класса закрыты тем, что нумерация здесь одна.
 *
 * **Длинная реплика РЕЖЕТСЯ, а не обрезается при хранении.** Реплика приходит
 * от вызывающего, и её длина ничем не ограничена: выгрузка чата без
 * разделителей строк — это одна реплика во весь разговор. Хранить её целиком
 * нельзя (в jsonb уехали бы три полных транскрипта PII на сигнал, с хранением
 * дольше самого транскрипта), а хранить НАЧАЛО — ещё хуже: подтверждающая фраза
 * может стоять в конце, и после удаления транскрипта в панели осталось бы
 * доказательство, которое ничего не доказывает (находка Codex на #964). Поэтому
 * режем ДО нумерации: модель видит куски по отдельности, ссылается на тот, где
 * фраза, и хранимый текст совпадает с прочитанным дословно.
 */
export function prepareTurns(
  raw: readonly {
    speaker: 'agent' | 'client' | 'unknown';
    text: string;
    sourceIndex?: number;
  }[]
): { turns: CriterionTranscriptTurn[]; index: CriterionTurnIndex } {
  const turns: CriterionTranscriptTurn[] = [];
  const byNumber = new Map<number, IndexedTurn>();
  for (const source of raw) {
    // Пустая реплика не печатается: номер у неё был бы, сослаться не на что.
    for (const chunk of chunkByWords(flattenTurnText(source.text), MAX_TURN_CHARS)) {
      if (chunk === '') continue;
      const turn = turns.length + 1;
      turns.push({
        turn,
        speaker: source.speaker,
        text: chunk,
        // Проносится дальше, а не вычисляется здесь: откуда взялась реплика,
        // знает только вызывающий, а номер — только эта функция.
        ...(source.sourceIndex !== undefined ? { sourceIndex: source.sourceIndex } : {}),
      });
      byNumber.set(turn, { turn, speaker: source.speaker, quote: chunk });
    }
  }
  return { turns, index: { byNumber, size: byNumber.size } };
}

/**
 * Превратить ссылки модели в заземлённые доказательства.
 *
 * Ответ БЕЗ значения доказательств не хранит вовсе — ни «неприменимо», ни «не
 * уверен»: доказательство чего? Это нормализация, а не отказ: схема ответа
 * принимает ссылки и при них (модель могла посмотреть реплику и не решить), а
 * отбрасывает их одно это место. Отвергать из-за них весь ответ значило бы
 * лишить критерий сигнала целиком из-за поля, которое всё равно не хранится.
 *
 * Номер вне диапазона отправленного транскрипта отбрасывается — это ровно тот
 * случай, который раньше назывался «цитата не нашлась». Повтор одного номера
 * схлопывается: три ссылки на одну реплику — одно доказательство, иначе ответ
 * выглядел бы подтверждённым трижды. Лишние сверх потолка отбрасываются здесь
 * же: strict-схема провайдера столько не пропустит, а дамп или иной путь —
 * может. `speaker` — только реплики этой стороны (встроенный с
 * `evidenceSpeaker`, #2784).
 */
export function groundEvidence(
  index: CriterionTurnIndex,
  answer: { value: unknown; evidence: readonly { turn: number }[] },
  { speaker }: { speaker?: GroundedEvidence['speaker'] } = {}
): GroundedEvidence[] {
  if (answer.value === null) return [];
  const grounded: GroundedEvidence[] = [];
  const used = new Set<number>();
  for (const reference of answer.evidence) {
    if (used.has(reference.turn)) continue;
    const turn = index.byNumber.get(reference.turn);
    if (!turn) continue;
    // Реплика не той стороны — до потолка: иначе три чужие ссылки вытеснили
    // бы четвёртую, свою.
    if (speaker !== undefined && turn.speaker !== speaker) continue;
    used.add(reference.turn);
    grounded.push({ turn: turn.turn, speaker: turn.speaker, quote: turn.quote });
    if (grounded.length >= MAX_EVIDENCE_PER_ANSWER) break;
  }
  return grounded;
}
